import { useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useEmulation } from '../contexts/EmulationContext'

/**
 * Returns auth data that reflects the emulated user when emulation is active.
 * When not emulating, passes through the real auth data unchanged.
 *
 * Use this instead of useAuth() in any component that renders permission-gated UI.
 * The real useAuth() should still be used for auth actions (login, logout, etc.)
 * and in contexts where you always need the real user (e.g. EmulationContext itself).
 */
export const useEffectiveAuth = () => {
  const auth = useAuth()
  const { isEmulating, emulatedUser } = useEmulation()

  const effectiveHasCapability = useCallback((capability) => {
    if (isEmulating && emulatedUser) {
      return (emulatedUser.capabilities || []).includes(capability)
    }
    return auth.hasCapability(capability)
  }, [isEmulating, emulatedUser, auth.hasCapability])

  const effectiveUser = useMemo(() => {
    if (!isEmulating || !emulatedUser) return auth.user
    return {
      ...auth.user,
      role: emulatedUser.role,
      isTenantAdmin: emulatedUser.role === 'admin',
      isSuperAdmin: false,
      isPlatformAdmin: false,
    }
  }, [isEmulating, emulatedUser, auth.user])

  const effectiveProfile = useMemo(() => {
    if (!isEmulating || !emulatedUser) return auth.profile
    return {
      ...auth.profile,
      role: emulatedUser.role,
      capabilities: emulatedUser.capabilities || [],
    }
  }, [isEmulating, emulatedUser, auth.profile])

  const effectiveCapabilities = useMemo(() => {
    if (isEmulating && emulatedUser) {
      return emulatedUser.capabilities || []
    }
    return auth.capabilities
  }, [isEmulating, emulatedUser, auth.capabilities])

  if (!isEmulating || !emulatedUser) {
    return auth
  }

  return {
    ...auth,
    user: effectiveUser,
    profile: effectiveProfile,
    capabilities: effectiveCapabilities,
    hasCapability: effectiveHasCapability,
  }
}
